import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { User } from '../types';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';
import { isPublicRoute } from '../constants/routes';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [lastCheckedPath, setLastCheckedPath] = useState<string | null>(null);
  const [justLoggedIn, setJustLoggedIn] = useState(false); // Flag para indicar que acabamos de hacer login

  useEffect(() => {
    const isPublic = isPublicRoute(location.pathname);
    
    // Si acabamos de hacer login y tenemos usuario, no hacer petición adicional
    // Esto evita problemas con cookies en móviles y previene que se resetee el usuario
    // IMPORTANTE: Verificar esto ANTES de la lógica de rutas públicas
    if (justLoggedIn && user !== null) {
      setIsLoading(false);
      setLastCheckedPath(location.pathname);
      // NO resetear justLoggedIn aquí, se reseteará después de navegar
      return;
    }
    
    // Si estamos en una ruta pública, no intentar obtener usuario
    // PERO no resetear el usuario si acabamos de hacer login (evita race condition)
    if (isPublic) {
      if (lastCheckedPath !== location.pathname) {
        setIsLoading(false);
        // Solo resetear usuario si NO acabamos de hacer login
        // Esto previene que se borre el usuario justo después del login
        if (!justLoggedIn) {
          setUser(null);
          setJustLoggedIn(false); // Reset flag al ir a ruta pública
        }
        setLastCheckedPath(location.pathname);
      }
      return;
    }
    
    // Solo verificar si no hemos verificado esta ruta antes o si cambiamos de ruta pública a privada
    if (lastCheckedPath === location.pathname && user !== null) {
      return;
    }
    
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
      } catch (error: any) {
        // No hay token válido o expirado, usuario no autenticado
        // Si es un error 429 (rate limit), esperar un poco antes de reintentar
        if (error?.response?.status === 429) {
          // Esperar 5 segundos antes de marcar como no autenticado
          // para evitar loops de rate limiting
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
        setUser(null);
      } finally {
        setIsLoading(false);
        setLastCheckedPath(location.pathname);
        setJustLoggedIn(false); // Reset flag
      }
    };

    initAuth();
  }, [location.pathname, lastCheckedPath, user, justLoggedIn]);

  const login = async (email: string, password: string): Promise<User> => {
    // Los tokens ahora se almacenan en cookies httpOnly automáticamente
    const loginResponse = await apiService.login({ email, password });
    
    // El backend ahora devuelve los datos del usuario en la respuesta del login
    // Esto evita la necesidad de una segunda petición que puede fallar en móviles
    if (loginResponse.user) {
      setUser(loginResponse.user);
      setJustLoggedIn(true); // Marcar que acabamos de hacer login
      setIsLoading(false); // Ya tenemos el usuario, no necesitamos loading
      return loginResponse.user;
    }
    
    // Fallback: si por alguna razón no vienen los datos del usuario, intentar obtenerlos
    // (esto no debería ser necesario, pero lo mantenemos por compatibilidad)
    const maxAttempts = 3;
    const delay = 100; // 100ms entre intentos
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Pequeño delay después del primer intento para dar tiempo a las cookies
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        return userData;
      } catch (error) {
        // Si es el último intento, lanzar el error
        if (attempt === maxAttempts - 1) {
          throw error;
        }
        // Continuar con el siguiente intento
      }
    }
    
    throw new Error('No se pudo obtener el usuario después del login');
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      logger.error('Error al hacer logout:', error);
    }
    setUser(null);
  };

  const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
    await apiService.register({ email, password, first_name: firstName, last_name: lastName });
  };

  const refreshUser = async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      // Actualizar fondo si el usuario tiene preferencia
      if (userData.background_image) {
        localStorage.setItem('background_image', userData.background_image);
      }
    } catch (error) {
      logger.error('Error al refrescar usuario:', error);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.role_name) return false;
    // Por ahora, solo verificamos permisos básicos según el rol
    // En producción, esto debería venir del backend
    if (user.role_name === 'super_admin') return true;
    if (user.role_name === 'admin') {
      // Admin tiene todos los permisos excepto PROMOTE_USERS
      // Pero también tiene MANAGE_USERS y MANAGE_ROLES
      return permission !== 'PROMOTE_USERS';
    }
    return false;
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(perm => hasPermission(perm));
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    refreshUser,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

